'use client';

import { Message } from '@/lib/copilot/message';
import classNames from 'classnames';
import { ReactNode, memo } from 'react';
import { parse } from 'marked';
import { Body } from '@/components/wysiwyg/Body';

import { MessageActions, MessageLinks } from './MessageUtils';

type AssistantMessageProps = {
  message: Message;
  isLast: boolean;
};


/**
 * Provides controls for the assistant message (copy, like/dislike, retry)
 */
function AssistantMessageOptions({ children, message, isLast, copyId }: {
  children: ReactNode;
  message: Message;
  isLast: boolean;
  copyId: string;
}) {
 
  // If the message is still being generated, don't show controls
  if (message.status === 'in_progress') {
    return <>{children}</>;
  }

  message.addMetadata('actions', ['copy', 'like', 'dislike', 'retry']);

  return (
    <div className="flex flex-col gap-2 group relative">
      {children}
      <MessageActions message={message} isLast={isLast} copyId={copyId} />
    </div>
  );
}


/**
 * Renders messages from the assistant/agent with controls for feedback and copying
 */
function AssistantMessageComponent({ message, isLast }: AssistantMessageProps) {
  const messageId = message.id + '-copy'

  return (
    <AssistantMessageOptions message={message} isLast={isLast} copyId={messageId}>
      <div 
        className={classNames(
          'bg-gray-400 text-black mr-auto',
          'p-4 rounded-lg w-fit'
        )}
      >
        <p className="sr-only">Acquia Copilot: </p>
        <div id={messageId} className="max-w-lg">
          <Body value={parse(message.content ?? '', {async: false}) as string} />
        </div>
        <MessageLinks links={message.links} />
      </div>
    </AssistantMessageOptions>
  );
}

export const AssistantMessage = memo(AssistantMessageComponent);